import {DestroyRef, inject, Injectable, signal, WritableSignal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AbstractControl, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {distinctUntilChanged, fromEvent, merge} from 'rxjs';
import {UserFormValidator} from '../components/user-form/data/user-form.validator';
import {CommunicationPreference, UserFormModel} from '../models/user-form.model';
import {IUserFormStruct, UserFormControlSections, UserFormInitialState, UserFormRole, UserFormSection, UserFormSectionFields, UserFormSectionValidityFn} from '../models/user-form.types';
import {UserFormStatus} from '../typings';
function getSectionFields<T extends UserFormSection>(section: T): UserFormSectionFields<T>[] {
	return Object.keys(UserFormInitialState[section]) as UserFormSectionFields<T>[];
}

@Injectable()
export class UserFormFactory {
	private readonly fb: FormBuilder = inject(FormBuilder);
	private readonly destroyRef = inject(DestroyRef);

	public userForm: FormGroup<{[K in keyof Partial<IUserFormStruct>]: FormControl<Partial<IUserFormStruct>[K]>}> = this.fb.group({});
	private userFormModel: UserFormModel;
	public formControlElements: Record<string, HTMLElement> = {};

	public readonly visibleSections$ = signal<UserFormSection[]>(null);

	private validityFns: Partial<Record<UserFormSection, UserFormSectionValidityFn>> = {};
	public readonly formSectionValidity$ = signal<Partial<Record<UserFormSection, ReturnType<UserFormSectionValidityFn>>>>({});

	// ------------ Indicators ------------
	public readonly formCreated = signal<boolean>(false);
	public formStatus: WritableSignal<UserFormStatus> = null;

	public createUserForm(sections: UserFormSection[], userFormModel: UserFormModel, formStatus: WritableSignal<UserFormStatus>) {
		this.formStatus = formStatus;
		this.userFormModel = userFormModel;
		this.visibleSections$.set(sections);
		sections.forEach(section => {
			switch (section) {
				case 'GeneralDetails':
					this.GeneralDetailsSection.addGeneralDetailsToForm(this.userForm, userFormModel);
					break;
				case 'ContactInfo':
					this.ContactInfoSection.addContactInfoToForm(this.userForm, userFormModel);
					break;
				case 'Password':
					this.PasswordSection.addPasswordToForm(this.userForm, userFormModel);
					break;
				case 'Permissions':
					this.PermissionsSection.addPermissionsToForm(this.userForm, userFormModel);
					break;
				case 'ActiveUserPreferences':
					this.ActiveUserPreferencesSection.addActiveUserPreferencesToForm(this.userForm, userFormModel);
					break;
				case 'ActiveUserStamps':
					this.ActiveUserStampsSection.addActiveUserStampsToForm(this.userForm, userFormModel);
					break;
				default:
					break;
			}
		});
		// Initialize validity check
		this.subscribeToSectionValidityChanges();

		this.formCreated.set(true);
		formStatus.set('working');
	}

	public getUpdateUserForm(): UserFormModel {
		const userValue = this.userForm.getRawValue();
		const userFormModel = this.userFormModel;
		userFormModel.updateFrom(userValue);
		return userFormModel;
	}

	public updateValidity(): void {
		const visibleSections = [...this.visibleSections$()];
		const formValidity = visibleSections.reduce(
			(acc, section) => {
				acc[section] = this.validityFns[section](section, this.userForm);
				return acc;
			},
			{} as Partial<Record<UserFormSection, {valid: boolean; dirty: boolean; touched: boolean}>>,
		);
		this.formSectionValidity$.set(formValidity);
	}

	public subscribeToSectionValidityChanges(): void {
		this.userForm.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.updateValidity();
		});
		this.updateValidity();
	}

	// --------  Add controls to form  --------

	public readonly GeneralDetailsSection = {
		addGeneralDetailsToForm: (userFormGroup: FormGroup, user: UserFormModel) => {
			const generalDetailsInitialState = UserFormInitialState.GeneralDetails;
			userFormGroup.addControl(
				'username',
				this.fb.control(user.userModel.username || generalDetailsInitialState.username, [Validators.required, Validators.minLength(2), UserFormValidator.usernameValidator]),
			);
			userFormGroup.addControl('fullname', this.fb.control(user.userModel.fullname || generalDetailsInitialState.fullname, [Validators.minLength(2)]));
			userFormGroup.addControl('title', this.fb.control(user.userModel.title || generalDetailsInitialState.title));
			userFormGroup.addControl('company', this.fb.control(user.userModel.company || generalDetailsInitialState.company));
			userFormGroup.addControl('department', this.fb.control(user.userModel.department || generalDetailsInitialState.department));
			userFormGroup.addControl('notes', this.fb.control(user.userModel.notes || generalDetailsInitialState.notes));

			// Add Section Validity Function
			this.validityFns.GeneralDetails = (section, form) => {
				const sectionFields = getSectionFields(section);
				return {
					valid: sectionFields.every(field => form.get(field)?.valid ?? false),
					dirty: sectionFields.some(field => form.get(field)?.dirty ?? false),
					touched: sectionFields.some(field => form.get(field)?.touched ?? false),
				};
			};
		},
	};

	public readonly ContactInfoSection = {
		addContactInfoToForm: (userFormGroup: FormGroup, user: UserFormModel) => {
			const contactInfoInitialState = UserFormInitialState.ContactInfo;
			userFormGroup.addControl('email', this.fb.control(user.userModel.email || contactInfoInitialState.email, [Validators.email]));
			userFormGroup.addControl('phonenumber', this.fb.control(user.userModel.phonenumber || contactInfoInitialState.phonenumber, [UserFormValidator.phoneNumberValidator]));
			userFormGroup.addControl('communicationPreference', this.fb.control(user.communicationPreference, UserFormValidator.requireAtLeastOneCheckbox));

			// Add Section Validity Function
			this.validityFns.ContactInfo = (section, form) => {
				const sectionFields = getSectionFields(section);
				const fieldsValid = sectionFields.every(field => form.get(field)?.valid ?? false);

				// בודק האם יש שגיאת phoneOrEmailRequired בטופס (רק אם הולידטור הזה הופעל)
				const hasPhoneOrEmailValidator = form.errors && 'phoneOrEmailRequired' in form.errors;

				return {
					valid: fieldsValid && (!hasPhoneOrEmailValidator || !form.hasError('phoneOrEmailRequired')),
					dirty: sectionFields.some(field => form.get(field)?.dirty ?? false),
					touched: sectionFields.some(field => form.get(field)?.touched ?? false),
				};
			};

			// כאשר מוזן טלפון / מייל - הראשון מביניהם - יסומן באופן אוטומטי email/sms
			const phoneControl = userFormGroup.get('phonenumber');
			const emailControl = userFormGroup.get('email');
			const communicationPreferenceControl = userFormGroup.get('communicationPreference');
			merge(phoneControl.valueChanges, emailControl.valueChanges)
				.pipe(
					takeUntilDestroyed(this.destroyRef),
					// filter(() => communicationPreferenceControl.value === null),
				)
				.subscribe(() => {
					if (communicationPreferenceControl.value === null && phoneControl.value && phoneControl.valid) {
						communicationPreferenceControl.setValue(CommunicationPreference.notifySMS);
					} else if (communicationPreferenceControl.value === null && emailControl.value && emailControl.valid) {
						communicationPreferenceControl.setValue(CommunicationPreference.notifyEmail);
					} else {
						communicationPreferenceControl.setValue(null);
					}
					communicationPreferenceControl.updateValueAndValidity();
					this.userForm.updateValueAndValidity();
				});
		},
		setPhoneOrEmailAsRequired: () => {
			// it's add new validator to the form that notifies the user that phone or email is required
			this.userForm.addValidators([UserFormValidator.phoneOrEmailRequired]);
			this.userForm.updateValueAndValidity();
		},
	};

	// ------------ Password ------------

	public readonly PasswordSection = {
		requirements: UserFormValidator.getPasswordRequirementsCopy(),
		validators: {
			password: [
				Validators.required,
				Validators.minLength(8),
				// Keep the comprehensive regex for final validation
				Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&^#()_+=\-[\]{};':"\\|,.<>/~`])[A-Za-z\d@$!%*?&^#()_+=\-[\]{};':"\\|,.<>/~`]{8,}$/),
			],
			confirmPassword: [Validators.required],
			passwordsMatch: [UserFormValidator.passwordsMatch],
		},
		addPasswordToForm: (userFormGroup: FormGroup, user: UserFormModel) => {
			const passwordInitialState = UserFormInitialState.Password;
			userFormGroup.addControl('password', this.fb.control(user.password || passwordInitialState.password, this.PasswordSection.validators.password));
			userFormGroup.addControl('confirmPassword', this.fb.control(user.password || passwordInitialState.confirmPassword, this.PasswordSection.validators.confirmPassword));

			userFormGroup.addValidators(this.PasswordSection.validators.passwordsMatch);

			// Add Section Validity Function
			this.validityFns.Password = (section, form) => {
				const passwordControls = getSectionFields(section);
				const passwordValid = passwordControls.every(field => form.get(field)?.valid ?? false);
				const passwordMatch = !form.hasError('passwordsNotMatch');
				return {
					valid: passwordValid && passwordMatch,
					dirty: passwordControls.some(field => form.get(field)?.dirty ?? false),
					touched: passwordControls.some(field => form.get(field)?.touched ?? false),
				};
			};

			const passwordControl = userFormGroup.get('password');
			const confirmPasswordControl = userFormGroup.get('confirmPassword');
			merge(passwordControl.valueChanges, confirmPasswordControl.valueChanges)
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					this.PasswordSection.requirements.forEach(req => {
						req.isValid = req.check(passwordControl.value);
					});
				});
		},
		setPasswordOptional: () => {
			this.PasswordSection.validators.password = this.PasswordSection.validators.password.filter(v => v !== Validators.required);
			this.PasswordSection.validators.confirmPassword = this.PasswordSection.validators.confirmPassword.filter(v => v !== Validators.required);
			this.PasswordSection.validators.passwordsMatch = this.PasswordSection.validators.passwordsMatch.filter(v => v !== UserFormValidator.passwordsMatch);
			this.userForm.updateValueAndValidity();
			return true;
		},
	};

	// ------------ Permissions ------------

	public readonly PermissionsSection = {
		userFormRoles: Object.keys(UserFormRole) as UserFormRole[],
		addPermissionsToForm: (userFormGroup: FormGroup, user: UserFormModel) => {
			const permissionsInitialState = UserFormInitialState.Permissions;
			const permissionsControls: Record<UserFormRole, FormControl<boolean>> = this.PermissionsSection.userFormRoles.reduce(
				(acc, role) => {
					acc[role] = this.fb.control(user.userModel.roles.includes(role) || permissionsInitialState.permissions.includes(role));
					return acc;
				},
				{} as Record<UserFormRole, FormControl<boolean>>,
			);
			userFormGroup.addControl('permissions', this.fb.group(permissionsControls, {validators: UserFormValidator.requireAtLeastOneCheckbox}));
			// Add Section Validity Function
			this.validityFns.Permissions = (section, form) => {
				const sectionFields = getSectionFields(section);
				return {
					valid: sectionFields.every(field => form.get(field)?.valid ?? false),
					dirty: sectionFields.some(field => form.get(field)?.dirty ?? false),
					touched: sectionFields.some(field => form.get(field)?.touched ?? false),
				};
			};

			/**
			
			 * - אם מוגדר משתמש חותם - הוא היחיד שיכול להיות מוגדר (השאר מתאפסים)
			 * - אם מוגדר משתמש רגיל - הוא יכול להיות לבד או עם משתמש סופר
			 * - משתמש סופר חייב להיות עם משתמש רגיל (אם מבטלים סימון ממשתמש רגיל, סופר יימחק)
			 */
			const permissionsControl: AbstractControl<Record<UserFormRole, boolean>> = userFormGroup.get('permissions');

			// מימוש הרשאות לפי הכללים המתוארים
			let previousValue: Record<UserFormRole, boolean> = {...permissionsControl.value};

			permissionsControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged()).subscribe(permissions => {
				const currentValue = {...permissions};
				const signerEnabled = currentValue[UserFormRole.Signer];
				const userEnabled = currentValue[UserFormRole.User];
				const superuserEnabled = currentValue[UserFormRole.Superuser];

				const previousSignerEnabled = previousValue[UserFormRole.Signer];

				// בדיקה איזה שדה השתנה
				const changedField = Object.keys(currentValue).find(key => currentValue[key as UserFormRole] !== previousValue[key as UserFormRole]) as UserFormRole;

				// אם לחצו על משתמש חותם
				if (changedField === UserFormRole.Signer && signerEnabled) {
					// אם החותם נלחץ והופעל, ביטול כל השאר
					currentValue[UserFormRole.User] = false;
					currentValue[UserFormRole.Superuser] = false;
					permissionsControl.setValue(currentValue, {emitEvent: false});
				}
				// אם לחצו על אחד מהתפקידים האחרים כשחותם מסומן
				else if (previousSignerEnabled && (changedField === UserFormRole.User || changedField === UserFormRole.Superuser)) {
					// ביטול החותם
					currentValue[UserFormRole.Signer] = false;

					// אם לחצו על משתמש סופר, צריך להפעיל גם את המשתמש הרגיל
					if (changedField === UserFormRole.Superuser && currentValue[UserFormRole.Superuser]) {
						currentValue[UserFormRole.User] = true;
					}

					permissionsControl.setValue(currentValue, {emitEvent: false});
				}
				// אם לחצו על משתמש סופר והפעילו אותו
				else if (changedField === UserFormRole.Superuser && superuserEnabled) {
					// הפעלת משתמש רגיל אוטומטית אם הוא לא מסומן
					if (!userEnabled) {
						currentValue[UserFormRole.User] = true;
						permissionsControl.setValue(currentValue, {emitEvent: false});
					}
				}
				// משתמש סופר חייב להיות עם משתמש רגיל - מקרה כללי כשמבטלים משתמש רגיל
				else if (!userEnabled && superuserEnabled && changedField === UserFormRole.User) {
					// אם משתמש רגיל בוטל ומשתמש סופר מופעל - ביטול סופר
					currentValue[UserFormRole.Superuser] = false;
					permissionsControl.setValue(currentValue, {emitEvent: false});
				}

				// שמירת המצב הנוכחי לבדיקה הבאה
				previousValue = {...currentValue};
			});
		},
	};

	// ------------ Active User Preferences ------------

	public readonly ActiveUserPreferencesSection = {
		addActiveUserPreferencesToForm: (userFormGroup: FormGroup, user: UserFormModel) => {
			const activeUserPreferencesInitialState = UserFormInitialState.ActiveUserPreferences;
			userFormGroup.addControl('dateFormat', this.fb.control(user.userModel.preferences.dateFormat || activeUserPreferencesInitialState.dateFormat));
			userFormGroup.addControl('daysToReminder', this.fb.control(user.userModel.preferences.daysToReminder || activeUserPreferencesInitialState.daysToReminder));
			userFormGroup.addControl('penColor', this.fb.control(user.userModel.preferences.penColor || activeUserPreferencesInitialState.penColor));
			userFormGroup.addControl('penWidth', this.fb.control(!isNaN(user.userModel.preferences.penWidth) ? user.userModel.preferences.penWidth : activeUserPreferencesInitialState.penWidth));

			// Add Section Validity Function
			this.validityFns.ActiveUserPreferences = (section, form) => {
				const sectionFields = getSectionFields(section);
				return {
					valid: sectionFields.every(field => form.get(field)?.valid ?? false),
					dirty: sectionFields.some(field => form.get(field)?.dirty ?? false),
					touched: sectionFields.some(field => form.get(field)?.touched ?? false),
				};
			};
		},
	};

	// ------------ Active User Stamps ------------

	public readonly ActiveUserStampsSection = {
		addActiveUserStampsToForm: (userFormGroup: FormGroup, user: UserFormModel) => {
			const activeUserStampsInitialState = UserFormInitialState.ActiveUserStamps;
			userFormGroup.addControl('defaultStamp', this.fb.control(user.userModel.preferences.defaultStamp || activeUserStampsInitialState.defaultStamp));

			// Add Section Validity Function
			this.validityFns.ActiveUserStamps = (section, form) => {
				const sectionFields = getSectionFields(section);
				return {
					valid: sectionFields.every(field => form.get(field)?.valid ?? false),
					dirty: sectionFields.some(field => form.get(field)?.dirty ?? false),
					touched: sectionFields.some(field => form.get(field)?.touched ?? false),
				};
			};
		},
	};

	// ----------- Public Methods -----------
	public isInvalidFnFactory = <S extends UserFormSection = UserFormSection>(options: Partial<{checkTouched: boolean; checkDirty: boolean; checkSubmitted: boolean}> = {}) => {
		return (controlName: UserFormSectionFields<S>) => {
			if (!this.userForm.controls || !this.formCreated()) return false;
			const control = this.userForm.get(controlName as string);
			const checkTouched = options.checkTouched ?? true;
			const checkDirty = options.checkDirty ?? true;
			const checkSubmitted = options.checkSubmitted ?? true;
			return !!control && control.invalid && ((checkTouched && control.touched) || (checkDirty && control.dirty) || (checkSubmitted && this.formStatus() === 'submitting'));
		};
	};

	public readonly hasErrorFnFactory = (errorName: string) => {
		return () => {
			if (!this.userForm.controls || !this.formCreated()) {
				return false;
			}
			return this.userForm.hasError(errorName);
		};
	};

	/**
	 * מסמן את כל השדות בסקשן מסוים כ-touched
	 * @param section הסקשן שיש לסמן
	 */
	private readonly markSectionAs = (section: UserFormSection, as: 'touched' | 'dirty'): void => {
		if (!section || !this.userForm.controls || !this.formCreated()) {
			return;
		}
		// if already marked, return
		if (this.userForm.get(section)?.touched || this.userForm.get(section)?.dirty) {
			return;
		}

		// משיג את שמות השדות בסקשן
		const sectionFields = getSectionFields(section);

		// מסמן כל שדה כ-touched
		sectionFields.forEach(field => {
			const control = this.userForm.get(field as string);
			if (control) {
				switch (as) {
					case 'touched':
						control.markAsTouched();
						break;
					case 'dirty':
						control.markAsDirty();
						break;
				}
			}
		});

		// עדכון הולידציה כדי לשקף את השינויים
		this.updateValidity();
	};

	public registerFormControl(controlName: string, element: HTMLElement): void {
		this.formControlElements[controlName] = element;
		const section = UserFormControlSections[controlName];

		// if the element is a checkbox, add an event listener to it
		if (element.tagName.toLowerCase() === 'input' && element.getAttribute('type') === 'checkbox') {
			fromEvent(element, 'change')
				.pipe(takeUntilDestroyed(this.destroyRef))
				.subscribe(() => {
					this.markSectionAs(section, 'dirty');
				});
		}
		fromEvent(element, 'focus')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.markSectionAs(section, 'touched');
			});
		fromEvent(element, 'blur')
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.markSectionAs(section, 'dirty');
			});
	}
}
